import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

// Layout
import Layout from './components/Layout/Layout';

// Auth
import Login from './pages/Auth/Login';

// Dashboard
import Dashboard from './pages/Dashboard/Dashboard';

// RRHH Module
import RRHHDashboard from './pages/RRHH/RRHHDashboard';
import Empleados from './pages/RRHH/Empleados';
import Asistencia from './pages/RRHH/Asistencia';
import Licencias from './pages/RRHH/Licencias';
import Capacitaciones from './pages/RRHH/Capacitaciones';
import ControlMedico from './pages/RRHH/ControlMedico';
import EPP from './pages/RRHH/EPP';
import Turnos from './pages/RRHH/Turnos';

// Finanzas Module
import FinanzasDashboard from './pages/Finanzas/FinanzasDashboard';
import Impuestos from './pages/Finanzas/Impuestos';
import CuentasBancarias from './pages/Finanzas/CuentasBancarias';
import PagosCobranzas from './pages/Finanzas/PagosCobranzas';
import Presupuestos from './pages/Finanzas/Presupuestos';
import CostosObra from './pages/Finanzas/CostosObra';
import Liquidaciones from './pages/Finanzas/Liquidaciones';

// Administración Module
import AdminDashboard from './pages/Admin/AdminDashboard';
import Facturacion from './pages/Admin/Facturacion';
import Contratos from './pages/Admin/Contratos';
import Proveedores from './pages/Admin/Proveedores';
import OrdenesCompra from './pages/Admin/OrdenesCompra';
import Stock from './pages/Admin/Stock';
import PartesDiarios from './pages/Admin/PartesDiarios';

// Operaciones Module
import OperacionesDashboard from './pages/Operaciones/OperacionesDashboard';
import OrdenesTrabajo from './pages/Operaciones/OrdenesTrabajo';
import Equipos from './pages/Operaciones/Equipos';
import Mantenimiento from './pages/Operaciones/Mantenimiento';
import IncidentesHSE from './pages/Operaciones/IncidentesHSE';
import Avances from './pages/Operaciones/Avances';

// Context
import { AuthProvider } from './context/AuthContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#76b3b7',
      light: '#a5d4d7',
      dark: '#4a8a8e',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3a3a3a',
      light: '#4B4F58',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F2F5F7',
      paper: '#ffffff',
    },
    text: {
      primary: '#3a3a3a',
      secondary: '#4B4F58',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
    },
    info: {
      main: '#76b3b7',
      light: '#a5d4d7',
    },
    grey: {
      100: '#F5F5F5',
      200: '#F2F5F7',
      600: '#4B4F58',
      700: '#424242',
      800: '#3a3a3a',
      900: '#000000',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 500 },
    h6: { fontWeight: 500 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                
                {/* RRHH Routes */}
                <Route path="rrhh" element={<RRHHDashboard />} />
                <Route path="rrhh/empleados" element={<Empleados />} />
                <Route path="rrhh/asistencia" element={<Asistencia />} />
                <Route path="rrhh/licencias" element={<Licencias />} />
                <Route path="rrhh/capacitaciones" element={<Capacitaciones />} />
                <Route path="rrhh/control-medico" element={<ControlMedico />} />
                <Route path="rrhh/epp" element={<EPP />} />
                <Route path="rrhh/turnos" element={<Turnos />} />
                
                {/* Finanzas Routes */}
                <Route path="finanzas" element={<FinanzasDashboard />} />
                <Route path="finanzas/impuestos" element={<Impuestos />} />
                <Route path="finanzas/cuentas-bancarias" element={<CuentasBancarias />} />
                <Route path="finanzas/pagos-cobranzas" element={<PagosCobranzas />} />
                <Route path="finanzas/presupuestos" element={<Presupuestos />} />
                <Route path="finanzas/costos-obra" element={<CostosObra />} />
                <Route path="finanzas/liquidaciones" element={<Liquidaciones />} />
                
                {/* Administración Routes */}
                <Route path="administracion" element={<AdminDashboard />} />
                <Route path="administracion/facturacion" element={<Facturacion />} />
                <Route path="administracion/contratos" element={<Contratos />} />
                <Route path="administracion/proveedores" element={<Proveedores />} />
                <Route path="administracion/ordenes-compra" element={<OrdenesCompra />} />
                <Route path="administracion/stock" element={<Stock />} />
                <Route path="administracion/partes-diarios" element={<PartesDiarios />} />
                
                {/* Operaciones Routes */}
                <Route path="operaciones" element={<OperacionesDashboard />} />
                <Route path="operaciones/ordenes-trabajo" element={<OrdenesTrabajo />} />
                <Route path="operaciones/equipos" element={<Equipos />} />
                <Route path="operaciones/mantenimiento" element={<Mantenimiento />} />
                <Route path="operaciones/incidentes-hse" element={<IncidentesHSE />} />
                <Route path="operaciones/avances" element={<Avances />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
